import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, catchError, map, of, throwError } from 'rxjs';
import { CoreFormatService } from './core-format.service';
import { IEidosEnvironment } from './environment.interface';
import { EidosCompany } from '@eidos/models/eidos-company.model';
import { EidosConfigService } from './eidos-config.service';
import { EidosLogService } from './eidos-log.service';
import { EidosSecurityService } from './eidos-security.service';
import { LocalStorageKeys } from '@eidos/models/core-constant.model';
import { DynamicApiResponse, IDynamicApiResponse, instanceOfIDynamicApiResponse } from '@eidos/models/dynamic-api-response.model';
import { IMyBizApiResponse, MyBizApiResponse } from '@eidos/models/mybiz.model';
import { EMPTY_CONFIG } from '@app/core/models/configuration.model';
import { Router } from '@angular/router';

export interface HttpOptions {
  headers?: HttpHeaders | {
    [header: string]: string | string[];
  };
  context?: HttpContext;
  observe?: 'body';
  params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

export type CoreHttpMethod = "GET" | "POST" | "PUT" | "DELETE";

@Injectable({
  providedIn: 'root'
})
export class EidosBaseApiService {
  private readonly router = inject(Router);
  protected config: IEidosEnvironment | undefined;
  protected baseUrl: string;
  public urlAPI: string;
  protected staticUrlAPI?: string;
  protected apiVersion: string = "v1";
  protected urlAgencyCC?: string;
  public documentUrl?:string;
  protected headers: any = {};
  public userCompany:BehaviorSubject<string> = new BehaviorSubject<string>('');
  public listCompany:BehaviorSubject<EidosCompany[]> = new BehaviorSubject<EidosCompany[]>([]);

  constructor(
    protected coreFormatService: CoreFormatService,
    protected configService: EidosConfigService,
    protected eidosLogService: EidosLogService,
    protected dialog: MatDialog,
    protected eidosSecurityService: EidosSecurityService,
    protected http: HttpClient
  ) {
    this.baseUrl = EMPTY_CONFIG.baseUrl;
    this.urlAPI = EMPTY_CONFIG.urlAPI;
    this.configService.currentConfig.subscribe(config => {
      this.config = config;
    });

    this.eidosSecurityService.currentLoggedUser.asObservable().subscribe(u => {
      this.userCompany.next(u.company);
    });
  }

  protected sendCommand<T = any>(type: CoreHttpMethod, url: string, data?: any, customBaseUrl?: string, withCredentials?: boolean, useVersion?: boolean):Observable<any> {
    if (this.config) {
      let headers = this.getHeaders(null);
      if (data instanceof ArrayBuffer) {
        headers.delete('Content-Type');
      }

      if (this.config.tokenOnHeader) {
        let token = localStorage.getItem(LocalStorageKeys.TOKEN);
        if (token) headers = headers.set(LocalStorageKeys.TOKEN, token);
      }

      let options: HttpOptions = {
        headers: headers
      };

      if (withCredentials) {
        options.withCredentials = true;
      }

      let apiUrl = useVersion ? `${this.urlAPI}/${this.apiVersion}/${url}` : `${this.urlAPI}/${url}`;

      if (customBaseUrl) {
        apiUrl = `${customBaseUrl}/${url}`;
      }

      this.eventuallyConvertData(data);
      data = this.includeCompany(data);

      let dataAsQueryString = "";

      switch (type.toUpperCase()) {
        case 'GET':
          if (data) {
            dataAsQueryString = "?" + Object.keys(data).filter(key => data[key] != undefined).map(key => `${ key }=${encodeURIComponent(data[key])}`).join("&");
          }
          return this.http.get<Response>(apiUrl + dataAsQueryString, options);

        case "DELETE":
          if (data) {
            dataAsQueryString = "?" + Object.keys(data).filter(key => data[key] != undefined).map(key => `${ key }=${encodeURIComponent(data[key])}`).join("&");
          }
          return this.http.delete<Response>(apiUrl + dataAsQueryString, options);

        case 'POST':
          return this.http.post<Response>(apiUrl, data, options);

        case 'PUT':
          return this.http.put<Response>(apiUrl, data, options);

        default:
          return throwError(() => 'Method not implemented');
      };
    } else {
      return throwError(() => 'Current config has not been retrieved yet');
    }
  };

  protected eventuallyConvertData(data: any) {
    data = this.coreFormatService.DeeplyConvertDateTimeOfObject(data);
    return data;
  }

  protected includeCompany(data:any) {
    // console.log('ciccia')
    if(!data) data = {};
    //TODO: to be removed
    data['companyId'] = '999';
    //END TODO

    if(this.listCompany.value.length === 0) return data;
    if(this.userCompany.value) {
      const findItem = this.listCompany.value.find(l => l.code.toLowerCase() === this.userCompany.value.toLowerCase());
      if(findItem) data['companyId'] = Number(findItem.id);
      return data;
    } else {
      this.includeCompany(data);
    }
  }

  protected getEndPoint(type: string, controller: string, action: string) {
    return `${this.urlAPI}/${type}/${this.apiVersion}/${controller}/${action}`;
  }

  protected getHeaders(headers: any) {
    return new HttpHeaders({ ...headers, ...this.headers });
  }

  protected getErrorMessage(dynamicResponse: DynamicApiResponse) {
    return dynamicResponse.errors.map(err => {
      let strErr = '';
      if (err.ErrorCod) {
        strErr += '<span class="bold">' + err.ErrorCod + ':</span> ';
      }
      if (err.ErrorCod) {
        strErr += err.ErrorDesc;
      }
      return strErr;
    }).join('<br/>');
  }

  protected handleGenericError(error: HttpErrorResponse | IDynamicApiResponse | any): Observable<any> {

    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        this.eidosSecurityService.clearAuthentication();
      }

    } else if (typeof error === 'string' || error instanceof String) {

      if (error.toLowerCase().startsWith('unauthorized')) {
        this.eidosSecurityService.clearAuthentication();
      }

    } else if (instanceOfIDynamicApiResponse(error)) {
      return this.handleDynamicError(error);
    }

    return throwError(() =>{
      return error.error ?? error
    });
  }

  protected handleDynamicError(errorDynResponse: IDynamicApiResponse): Observable<IDynamicApiResponse> {
    return throwError(() => errorDynResponse);
  }

  public callDynamicAPI<T = any>(api: string, data?: any, method: CoreHttpMethod = "GET") {
    return this.sendCommand<T>(method, api, data, undefined, false, true)
      .pipe(
        map<IMyBizApiResponse | IDynamicApiResponse, DynamicApiResponse>((response) => {
          if (instanceOfIDynamicApiResponse(response)) {
            return new DynamicApiResponse(response);
          } else {
            this.router.navigate(['./login']);
            throw new MyBizApiResponse(response).error;
          }
        }),
        catchError((error: HttpErrorResponse | IDynamicApiResponse | any) => this.handleGenericError(error))
      );
  }

  public getDynamicAPI<T = any>(api: string, data?: any):Observable<DynamicApiResponse> {
    return this.callDynamicAPI<T>(api, data, "GET");
  }

  public postDynamicAPI(api: string, data: any) {
    return this.callDynamicAPI(api, data, "POST");
  }

  public putDynamicAPI(api: string, data: any) {
    return this.callDynamicAPI(api, data, "PUT");
  }

  public deleteDynamicAPI(api: string, data: any) {
    return this.callDynamicAPI(api, data, "DELETE");
  }

  public getStaticAPI(api: string, method: CoreHttpMethod, data?: any) {
    if (!!this.staticUrlAPI) {
      return this.sendCommand(method, api, data, this.staticUrlAPI, false, true)
        .pipe(
          catchError((error: HttpErrorResponse) => this.handleGenericError(error))
        );
    } else {
      return of(undefined);
    }
  }

  public getPublicAPI(api: string, method: 'GET' | 'DELETE' | 'POST' | 'PUT', data?: any) {
    return this.sendCommand(method, "general/" + api, data, undefined, false)
      .pipe(catchError((error: HttpErrorResponse) => this.handleGenericError(error)));
  }
}
